import { useEffect, useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import CopyIcon from '@mui/icons-material/CopyAll'
import DeleteIcon from '@mui/icons-material/Delete'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Toolbar from '@mui/material/Toolbar'

import StyledTable from '@components/StyledTable'
import { formatDateTime } from '@util/date-util'

import { StyledIconButton } from '../../theme/components/StyledIconButton'
import { useAlerts } from '../../theme/hooks/useAlerts'
import { useDeleteJobSchedule } from '../hooks/useDeleteJobSchedule'
import { useGetJobSchedules } from '../hooks/useGetJobSchedules'
import { useRequeueJobSchedule } from '../hooks/useRequeueJobSchedule'
import type { JobSchedule } from '../types/JobSchedule'

import { JobSchedulerRepeatInterval } from './JobScheduleRepeatInterval'
import { JobScheduleStatus } from './JobScheduleStatus'
import { CreateJobSchedule } from './dialogs/CreateJobSchedule'
import { JobScheduleData } from './dialogs/JobScheduleData'

const headerCells = [
  { id: 'status', numeric: false, label: 'Status' },
  { id: 'name', numeric: false, label: 'Name' },
  { id: 'lastRunAt', numeric: false, label: 'Last run started' },
  { id: 'nextRunAt', numeric: false, label: 'Next run starts' },
  { id: 'lastFinished', numeric: false, label: 'Last finished' },
  { id: 'filler' },
] as const

export const JobScheduler = () => {
  const { successAlert } = useAlerts()
  const { data, isLoading, error } = useGetJobSchedules()
  const [drawer, setDrawer] = useState(false)
  const [open, setOpen] = useState(false)
  const [selectedJobSchedule, setSelectedJobSchedule] = useState<JobSchedule>()
  const { mutate: deleteJob } = useDeleteJobSchedule()
  const { mutate: requeueJob } = useRequeueJobSchedule()

  useEffect(() => {
    if (data && data.length === 0) {
      setSelectedJobSchedule(undefined)
      setDrawer(false)
    } else if (data && data.length > 0 && selectedJobSchedule) {
      const jobSchedule = data.find(
        ({ _id }) => _id === selectedJobSchedule._id,
      )

      /* v8 ignore next -- @preserve */
      if (jobSchedule !== selectedJobSchedule) {
        setSelectedJobSchedule(jobSchedule)
      }
    }
  }, [data, selectedJobSchedule])

  const handleSelect = (job: JobSchedule) => {
    setSelectedJobSchedule(job)
    setDrawer(true)
  }

  const handleDelete = (ev: any, job: JobSchedule) => {
    ev.stopPropagation()
    deleteJob(job._id, {
      onSuccess: () => {
        successAlert('Job schedule succesvol verwijderd')
      },
    })
  }

  const handleCopy = (ev: any, job: JobSchedule) => {
    ev.stopPropagation()
    requeueJob(
      { name: job.name, data: job.data },
      {
        onSuccess: () => {
          successAlert('Job schedule succesvol requeued')
        },
      },
    )
  }

  const onClose = () => setDrawer(false)

  return (
    <>
      <Toolbar
        sx={{
          backgroundColor: '#F7F7F7',
          borderBottom: '1px solid #E0E0E0',
          justifyContent: 'flex-end',
        }}
      >
        <StyledIconButton
          title="Nieuw job schedule"
          onClick={() => setOpen(true)}
          icon={<AddIcon />}
        />
      </Toolbar>
      <StyledTable
        variant="details"
        headerCells={headerCells}
        loading={isLoading}
        isError={!!error}
        {...({} as any)}
      >
        {data.map((jobSchedule) => {
          const {
            _id,
            name,
            repeatInterval,
            nextRunAt,
            lastRunAt,
            lastFinishedAt,
          } = jobSchedule

          return (
            <TableRow key={_id} onClick={() => handleSelect(jobSchedule)}>
              <TableCell>
                {repeatInterval && (
                  <JobSchedulerRepeatInterval repeatInterval={repeatInterval} />
                )}
                <JobScheduleStatus jobSchedule={jobSchedule} />
              </TableCell>
              <TableCell>{name}</TableCell>
              <TableCell>{formatDateTime(lastRunAt)}</TableCell>
              <TableCell>{formatDateTime(nextRunAt)}</TableCell>
              <TableCell>{formatDateTime(lastFinishedAt)}</TableCell>
              <TableCell>
                <>
                  <StyledIconButton
                    onClick={(ev) => handleCopy(ev, jobSchedule)}
                    icon={<CopyIcon />}
                    title="Requeue job schedule"
                  />
                  <StyledIconButton
                    onClick={(ev) => handleDelete(ev, jobSchedule)}
                    icon={<DeleteIcon />}
                    title="Verwijder job schedule"
                  />
                </>
              </TableCell>
            </TableRow>
          )
        })}
      </StyledTable>

      {/* The selection stays after closing, so its data shows while closing */}
      {selectedJobSchedule && (
        <JobScheduleData
          jobSchedule={selectedJobSchedule}
          open={drawer}
          onClose={onClose}
        />
      )}
      {open && <CreateJobSchedule onClose={() => setOpen(false)} />}
    </>
  )
}
